/**
 * 手抄自 C02 的 typed REST client。
 *
 * 設計重點：
 * 1. **Idempotency-Key**：C02 §0 規定所有 POST 接受、出手**必須**帶。這裡由 client 統一產生。
 * 2. **Clock skew**：對局倒數是對著伺服器的 `deadline_at` 算的。瀏覽器時鐘可能歪掉幾秒，
 *    倒數就會不準。我們從每個 REST 回應的 `Date` header（CORS-safelisted，跨網域也讀得到）
 *    估算 skew，之後所有倒數都用 `serverNow()`。
 * 3. **不吞錯**：非 2xx 一律丟 `ApiError`，帶上 C02 §0 的 `error.code`，讓 UI 可以針對
 *    `ROUND_MISMATCH` / `ALREADY_SUBMITTED` 做正確處理。
 */

import type {
  ApiErrorBody,
  GameDetail,
  GamesResponse,
  GuestTokenResponse,
  InviteResponse,
  JoinResponse,
  LeaderboardRow,
  LobbyResponse,
  MatchView,
  Paged,
  QueueRequest,
  QueueTicketCreated,
  QueueTicketStatus,
  RematchResponse,
  ReplayResponse,
  SubmitActionRequest,
  SubmitActionResponse,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: Record<string, unknown>;
  readonly retryAfterMs: number | null;

  constructor(status: number, code: string, message: string, detail: Record<string, unknown> = {}, retryAfterMs: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfterMs = retryAfterMs;
  }

  /** 網路層失敗（DNS/離線/CORS），不是伺服器回的錯誤。 */
  static network(cause: unknown): ApiError {
    const msg = cause instanceof Error ? cause.message : String(cause);
    return new ApiError(0, 'NETWORK_ERROR', msg);
  }
}

export type TokenSupplier = () => string | null;

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: TokenSupplier;
  fetchImpl?: typeof fetch;
  /** 產生 Idempotency-Key，測試時可注入確定性版本。 */
  newIdempotencyKey?: () => string;
}

export function randomIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // 退路：不需要密碼學強度，只要夠唯一以避免重試撞號。
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** 某些端點（如 GET /games）是公開的，不需要也不該送 token。 */
  anonymous?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenSupplier;
  private readonly fetchImpl: typeof fetch;
  private readonly newIdempotencyKey: () => string;
  /** serverNow() - Date.now()，由回應的 Date header 估算。 */
  private clockSkewMs = 0;
  private clockSkewSamples = 0;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.fetchImpl = opts.fetchImpl ?? ((...args) => globalThis.fetch(...args));
    this.newIdempotencyKey = opts.newIdempotencyKey ?? randomIdempotencyKey;
  }

  /** 以伺服器時鐘為準的「現在」。倒數計時一律用這個，不要用 Date.now()。 */
  serverNow(): number {
    return Date.now() + this.clockSkewMs;
  }

  get skewMs(): number {
    return this.clockSkewMs;
  }

  private observeServerDate(res: Response): void {
    const raw = res.headers.get('date');
    if (!raw) return;
    const serverMs = Date.parse(raw);
    if (Number.isNaN(serverMs)) return;
    // Date header 只有秒級精度，取移動平均以免倒數抖動。
    const sample = serverMs - Date.now();
    this.clockSkewSamples += 1;
    const n = Math.min(this.clockSkewSamples, 10);
    this.clockSkewMs = this.clockSkewMs + (sample - this.clockSkewMs) / n;
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (!opts.anonymous) {
      const token = this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET' && method !== 'DELETE') {
      headers['Idempotency-Key'] = opts.idempotencyKey ?? this.newIdempotencyKey();
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
        credentials: 'omit', // token 走 Authorization header，不用 cookie → 天然免疫 CSRF
        mode: 'cors',
      });
    } catch (cause) {
      throw ApiError.network(cause);
    }

    this.observeServerDate(res);

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const body = parsed as ApiErrorBody | null;
      const retryAfter = res.headers.get('retry-after');
      throw new ApiError(
        res.status,
        body?.error?.code ?? `HTTP_${res.status}`,
        body?.error?.message ?? res.statusText ?? 'request failed',
        body?.error?.detail ?? {},
        retryAfter ? Number(retryAfter) * 1000 : null,
      );
    }

    return parsed as T;
  }

  // --- C02 §2 公開端點 ---------------------------------------------------

  listGames(signal?: AbortSignal): Promise<GamesResponse> {
    return this.request<GamesResponse>('/games', { anonymous: true, signal });
  }

  getGame(gameKey: string, signal?: AbortSignal): Promise<GameDetail> {
    return this.request<GameDetail>(`/games/${encodeURIComponent(gameKey)}`, { anonymous: true, signal });
  }

  getLeaderboard(
    gameKey: string,
    params: { variant_key?: string; window?: '24h' | '7d' | 'all' } = {},
    signal?: AbortSignal,
  ): Promise<Paged<LeaderboardRow>> {
    const qs = new URLSearchParams();
    if (params.variant_key) qs.set('variant_key', params.variant_key);
    if (params.window) qs.set('window', params.window);
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.request<Paged<LeaderboardRow>>(`/games/${encodeURIComponent(gameKey)}/leaderboard${suffix}`, {
      anonymous: true,
      signal,
    });
  }

  // --- C02 §3 認證 -------------------------------------------------------

  createGuest(displayName?: string): Promise<GuestTokenResponse> {
    return this.request<GuestTokenResponse>('/auth/guest', {
      method: 'POST',
      anonymous: true,
      body: displayName ? { display_name: displayName } : {},
    });
  }

  // --- C02 §4 大廳 -------------------------------------------------------

  getLobby(gameKey: string, signal?: AbortSignal): Promise<LobbyResponse> {
    return this.request<LobbyResponse>(`/lobby/${encodeURIComponent(gameKey)}`, { signal });
  }

  enqueue(gameKey: string, req: QueueRequest): Promise<QueueTicketCreated> {
    return this.request<QueueTicketCreated>(`/lobby/${encodeURIComponent(gameKey)}/queue`, {
      method: 'POST',
      body: req,
    });
  }

  cancelQueue(ticketId: string): Promise<void> {
    return this.request<void>(`/lobby/queue/${encodeURIComponent(ticketId)}`, { method: 'DELETE' });
  }

  getTicket(ticketId: string, signal?: AbortSignal): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>(`/lobby/queue/${encodeURIComponent(ticketId)}`, { signal });
  }

  createInvite(gameKey: string, variantKey: string): Promise<InviteResponse> {
    return this.request<InviteResponse>(`/lobby/${encodeURIComponent(gameKey)}/invite`, {
      method: 'POST',
      body: { variant_key: variantKey },
    });
  }

  joinByCode(inviteCode: string): Promise<JoinResponse> {
    return this.request<JoinResponse>(`/lobby/join/${encodeURIComponent(inviteCode)}`, { method: 'POST', body: {} });
  }

  // --- C02 §5 對局 -------------------------------------------------------

  getMatch(matchId: string, signal?: AbortSignal): Promise<MatchView> {
    return this.request<MatchView>(`/matches/${encodeURIComponent(matchId)}`, { signal });
  }

  /** 出手。C02 §0：**必須**帶 Idempotency-Key，避免網路重試造成重複出手。 */
  submitAction(matchId: string, req: SubmitActionRequest, idempotencyKey: string): Promise<SubmitActionResponse> {
    return this.request<SubmitActionResponse>(`/matches/${encodeURIComponent(matchId)}/actions`, {
      method: 'POST',
      body: req,
      idempotencyKey,
    });
  }

  forfeit(matchId: string): Promise<void> {
    return this.request<void>(`/matches/${encodeURIComponent(matchId)}/forfeit`, { method: 'POST', body: {} });
  }

  rematch(matchId: string): Promise<RematchResponse> {
    return this.request<RematchResponse>(`/matches/${encodeURIComponent(matchId)}/rematch`, { method: 'POST', body: {} });
  }

  getReplay(matchId: string, signal?: AbortSignal): Promise<ReplayResponse> {
    return this.request<ReplayResponse>(`/matches/${encodeURIComponent(matchId)}/replay`, { signal });
  }
}
